import { useEffect, useMemo, useRef, useState } from "react";

import { ClickAwayListener, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { palette, spacing } from "../../../../styles/theme";
import {
  AssetColor,
  PartElementStyleColorCombo,
  ProductDefinitionColor,
} from "../../../../types";
import { getRotationStyle } from "../../../../utils/themeUtils";
import { useSelector } from "../../../hooks/useSelector";
import { Box } from "../../Box";
import { Dot, Icon } from "../../Icon";
import { ColorPicker } from "../ColorPicker";
import InheritedSvg from "./assets/inherited.svg";
import TransparentSvg from "./assets/transparent.svg";
import EcoBlackSvg from "./assets/eco_black.svg";
import Heather004Img from "./assets/Heather_004.jpg";
import Heather003Img from "./assets/Heather_003.jpg";
import Heather002Img from "./assets/Heather_002.jpg";
import ColorBox from "./ColorBox/ColorBox";
import { useStudio } from "../../../../context/Studio";
import {
  PantoneColor,
  searchPantoneColors,
  sortPantoneColors,
  formatPantoneName,
  normalizeHexColor,
  isValidHexColor,
  sortColorsByDistance,
} from "../../../../utils/color.utils";
import { Input } from "../../Input";

interface Props {
  value?: string;
  onChange: (color: string, active?: boolean) => void;
  allowNoColors?: boolean;
  allowTransparent?: boolean;
  disableAllColors?: boolean;
  deprecateTransparency?: boolean;
  enableCustomColorSelection?: boolean;
  bleedingWarning: boolean;
  availableCombos?: (PartElementStyleColorCombo & { colorsHex: string[] })[];
  availableColors?: (ProductDefinitionColor | AssetColor)[];
  allColors?: (ProductDefinitionColor | AssetColor)[];
  isClearable?: boolean;
  isLayerActive?: boolean;
  showColorPicker?: boolean;
}

const ColorSelect = (props: Props) => {
  const {
    value,
    allowNoColors,
    allowTransparent = false,
    disableAllColors = false,
    deprecateTransparency = false,
    availableCombos = [],
    availableColors = [],
    allColors = [],
    enableCustomColorSelection = true,
    bleedingWarning = false,
    showColorPicker,
    isClearable,
    isLayerActive,
  } = props;

  const {
    designState: { isUpdating },
    isDesignLocked,
  } = useStudio();

  const [currentValue, setCurrentValue] = useState(value);

  const colorSelectRef = useRef<HTMLDivElement>(null);
  const colorBoxRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const onChange = (color: string, active?: boolean) => {
    setIsColorLoading(true);
    setCurrentValue(color);
    props.onChange(color, active);
    setIsDropdownOpen(false);
  };

  const [isColorLoading, setIsColorLoading] = useState(false);

  const { t, i18n } = useTranslation();

  const isHexValue = value?.startsWith("#");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedPantone, setSelectedPantone] = useState<PantoneColor | null>(
    null
  );

  const userInfo = useSelector((state) => state.userInfo);
  const isAdmin = userInfo.isAdmin || userInfo.isDesigner;

  const defaultColor = "#FFFFFF";
  const colorFromChart = allColors?.find((c) => c.name === value);
  const isColorDisabled = allColors?.find((c) => c.name === value)?.disabled;
  const colorFromChartCurrent = allColors?.find((c) => c.name === currentValue);

  const currentHexValue = useMemo(() => {
    if (availableCombos.length) {
      return (
        availableCombos.find((x) => x.name === value)?.colorsHex ?? defaultColor
      );
    }

    return isHexValue
      ? value || defaultColor
      : colorFromChart?.hex ||
          (colorFromChart as ProductDefinitionColor)?.fallback ||
          defaultColor;
  }, [availableCombos, colorFromChart, isHexValue, value]);

  useEffect(() => {
    if (value === currentValue && !isUpdating) {
      setIsColorLoading(false);
    }
  }, [value, currentValue, isUpdating]);

  useEffect(() => {
    allColors.forEach((color) => {
      new Image().src = (color as ProductDefinitionColor).textureUrl;
    });
  }, [allColors]);

  useEffect(() => {
    if (value !== currentValue) {
      setCurrentValue(value);
    }
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setSearchQuery("");
      setDebouncedSearchQuery("");
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchResults = useMemo(() => {
    const MAX_COLOR_DISTANCE = 20;
    const MAX_PANTONE_RESULTS = 50;

    if (
      !debouncedSearchQuery.trim() ||
      debouncedSearchQuery.trim().length < 3
    ) {
      return {
        colors: availableColors,
        searchType: null,
        exactMatch: null,
        pantoneColors: [],
      };
    }

    const query = debouncedSearchQuery.trim();

    let hexMatchedColors: (ProductDefinitionColor | AssetColor)[] = [];
    let hexExactMatch: ProductDefinitionColor | AssetColor | null = null;
    let isHexSearch = false;

    if (isValidHexColor(query)) {
      isHexSearch = true;
      const normalizedQuery = normalizeHexColor(query);

      const rankedColorMatches = sortColorsByDistance(
        normalizedQuery,
        availableColors,
        MAX_COLOR_DISTANCE
      );

      const exactMatchIndex = rankedColorMatches.findIndex(
        ({ color }) => normalizeHexColor(color.hex) === normalizedQuery
      );

      if (exactMatchIndex >= 0) {
        hexExactMatch = rankedColorMatches[exactMatchIndex].color;
        rankedColorMatches.splice(exactMatchIndex, 1);
      }

      hexMatchedColors = rankedColorMatches.map((item) => item.color);
    }

    const pantoneMatches = isHexSearch
      ? []
      : sortPantoneColors(searchPantoneColors(query, MAX_PANTONE_RESULTS));

    const hasPantoneMatches = pantoneMatches.length > 0;

    if (isHexSearch && (hexExactMatch || hexMatchedColors.length > 0)) {
      const finalColors = hexExactMatch
        ? [hexExactMatch, ...hexMatchedColors]
        : hexMatchedColors;
      const finalExactMatch = hexExactMatch || null;

      return {
        colors: finalColors,
        searchType: "hex",
        exactMatch: finalExactMatch,
        pantoneColors: [],
      };
    }

    if (hasPantoneMatches) {
      return {
        colors: [],
        searchType: "pantone",
        exactMatch: null,
        pantoneColors: pantoneMatches,
      };
    }

    return {
      colors: [],
      searchType: null,
      exactMatch: null,
      pantoneColors: [],
    };
  }, [debouncedSearchQuery, availableColors]);

  const matchedColor = useMemo(() => {
    if (
      searchResults.exactMatch &&
      (searchResults.searchType === "hex" ||
        searchResults.searchType === "mixed")
    ) {
      if (isValidHexColor(searchQuery)) {
        return searchResults.exactMatch;
      }
    }
    return null;
  }, [searchResults, searchQuery]);

  const searchedHexColor = useMemo(() => {
    if (searchQuery && isValidHexColor(searchQuery)) {
      return normalizeHexColor(searchQuery);
    }
    return null;
  }, [searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setSelectedPantone(null);
  };

  const handlePantoneSelect = (pantone: PantoneColor) => {
    setSearchQuery(pantone.hex);
    setSelectedPantone(pantone);
  };

  const toggle = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const toggleColorPicker = () => setIsColorPickerOpen((prev) => !prev);

  return (
    <Box position={"relative"} sx={{ background: "white" }}>
      <ClickAwayListener
        onClickAway={(e) => {
          const containsColorPicker = colorPickerRef.current?.contains(
            e.target as Node
          );
          if (!containsColorPicker && (isDropdownOpen || isColorPickerOpen)) {
            toggle();
            setIsColorPickerOpen(false);
          }
        }}
      >
        <Box position={"relative"}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            py={spacing.smallDesktop}
            sx={{
              cursor: "pointer",
              pointerEvents: isDesignLocked ? "none" : "auto",
            }}
            position={"relative"}
            onClick={isDesignLocked ? undefined : toggle}
            ref={colorSelectRef}
          >
            <Box display="flex" gap={spacing.smallDesktop}>
              {value &&
                isLayerActive &&
                (isColorLoading ? (
                  <Skeleton
                    variant="rounded"
                    width={22}
                    height={22}
                    sx={{
                      margin: "1px",
                      background: palette.grey.lightGrey,
                    }}
                  />
                ) : (
                  <ColorBox
                    colorOrCombo={currentHexValue}
                    size={22}
                    imgSrc={
                      (colorFromChart as ProductDefinitionColor)?.textureUrl
                    }
                  >
                    {value === "transparent" && (
                      <img src={TransparentSvg} alt="transparent" />
                    )}
                    {value === "inherited" && (
                      <img src={InheritedSvg} alt="inherited" />
                    )}
                    {ColorToImage[value] &&
                      !(colorFromChart as ProductDefinitionColor)
                        ?.textureUrl && (
                        <img src={ColorToImage[value]} alt={value} />
                      )}
                  </ColorBox>
                ))}
              <Box display="flex" alignItems={"center"}>
                <Typography variant="actionSmallIcon">
                  {value && isLayerActive
                    ? isHexValue
                      ? t(`colors.custom`)
                      : t(
                          `colors.${colorFromChart ? colorFromChart.label : value || "white"}`
                        )
                    : t(`colors.not_active`)}
                </Typography>
                {bleedingWarning ||
                  (isColorDisabled && (
                    <Dot color="warning" sx={{ ml: "5px" }} />
                  ))}
              </Box>
            </Box>
            {isDesignLocked ? null : isClearable && !!value ? (
              <Icon
                icon="close"
                size={24}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("", false);
                }}
              />
            ) : (
              <Icon
                icon="keyboard_arrow_down"
                size={24}
                style={{ ...getRotationStyle(isDropdownOpen) }}
              />
            )}
          </Box>
          {isDropdownOpen && (
            <>
              <Box
                ref={colorBoxRef}
                position={"relative"}
                top={"100%"}
                sx={{
                  background: "white",
                  zIndex: 1,
                  py: "12px",
                  boxShadow: "0px 1px 16px 0px rgba(16, 24, 40, 0.08)",
                  borderRadius: "8px",
                  mx: "2px",
                }}
              >
                <Box sx={{ mb: "12px", px: "12px", position: "relative" }}>
                  <Input
                    fullWidth
                    value={
                      selectedPantone
                        ? formatPantoneName(selectedPantone.name)
                        : searchQuery
                    }
                    onChange={(e) => {
                      if (selectedPantone) {
                        setSelectedPantone(null);
                      }
                      setSearchQuery(e.target.value);
                    }}
                    placeholder={t("studio.colors.search_placeholder")}
                    clearable
                    onClick={(e) => e.stopPropagation()}
                    onClear={handleClearSearch}
                    size="extraSmall"
                    startAdornment={
                      (matchedColor || searchedHexColor) && (
                        <Box
                          onClick={(e: any) => {
                            e.stopPropagation();
                            if (matchedColor) {
                              onChange(matchedColor.name);
                              handleClearSearch();
                            }
                          }}
                          sx={{
                            cursor: matchedColor ? "pointer" : "default",
                            display: "flex",
                            alignItems: "center",
                            mr: 1,
                          }}
                        >
                          <ColorBox
                            colorOrCombo={
                              matchedColor?.hex || searchedHexColor || ""
                            }
                            size={20}
                            imgSrc={
                              matchedColor
                                ? (matchedColor as ProductDefinitionColor)
                                    .textureUrl
                                : undefined
                            }
                          />
                        </Box>
                      )
                    }
                  />
                </Box>

                {searchQuery && searchQuery.trim().length >= 3 && (
                  <>
                    {searchResults.pantoneColors.length > 0 && (
                      <>
                        <Box mb="8px" px="12px">
                          <Typography variant="textSmBold">
                            {t("studio.colors.pantone")}
                          </Typography>
                        </Box>
                        <Box maxHeight="200px" overflow="auto" mb="12px">
                          {searchResults.pantoneColors.map((pantone, index) => (
                            <Box
                              key={index}
                              display="flex"
                              alignItems="center"
                              gap={"8px"}
                              sx={{
                                py: "6px",
                                px: "12px",
                                "&:hover": {
                                  background: palette.blue.paleEffect,
                                },
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                handlePantoneSelect(pantone);
                              }}
                            >
                              <ColorBox colorOrCombo={pantone.hex} size={22} />
                              <Typography variant="textSm">
                                {formatPantoneName(pantone.name)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                    {searchResults.colors.length > 0 && (
                      <Box mb="8px" px="12px">
                        <Typography variant="textSmBold">
                          {t("studio.colors.closest_matches")}
                        </Typography>
                      </Box>
                    )}
                    {searchResults.colors.length === 0 &&
                      searchResults.pantoneColors.length === 0 && (
                        <Box py="16px" px="12px" textAlign="center">
                          <Typography
                            variant="textSm"
                            color={palette.grey.darkerGrey}
                          >
                            {t("studio.colors.no_results")}
                          </Typography>
                        </Box>
                      )}
                  </>
                )}
                {(!searchQuery || searchQuery.trim().length < 3) && (
                  <>
                    <Box mb="8px" px="12px">
                      <Typography variant="textSmBold">
                        {t("studio.colors.title")}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexWrap={"wrap"}
                      alignItems={"center"}
                      gap={spacing.smallDesktop}
                      px="12px"
                    >
                      {allowNoColors && (
                        <ColorBox
                          colorOrCombo="white"
                          imgSrc={InheritedSvg}
                          isActive={
                            isLayerActive &&
                            "inherited" === colorFromChartCurrent?.name
                          }
                          disabled={disableAllColors}
                          onClick={() => onChange("inherited")}
                          tooltip={t(`colors.no_color`)}
                        />
                      )}
                      {allowTransparent && (
                        <ColorBox
                          colorOrCombo="white"
                          imgSrc={TransparentSvg}
                          isActive={
                            isLayerActive &&
                            "transparent" === colorFromChartCurrent?.name
                          }
                          disabled={disableAllColors || deprecateTransparency}
                          onClick={() => onChange("transparent")}
                          tooltip={t(`colors.transparent`)}
                        />
                      )}
                      {availableColors.map((option, index) => {
                        return (
                          <ColorBox
                            key={index}
                            colorOrCombo={option?.hex}
                            isActive={
                              isLayerActive &&
                              option?.name === colorFromChartCurrent?.name
                            }
                            disabled={
                              disableAllColors ||
                              (option as ProductDefinitionColor).hidden ||
                              (option as AssetColor).disabled
                            }
                            hidden={(option as ProductDefinitionColor).hidden}
                            onClick={() => {
                              onChange(option.name);
                              handleClearSearch();
                            }}
                            tooltip={
                              i18n.exists(
                                `pantone_reference.codes.${option.label}`
                              )
                                ? t("pantone_reference.label", {
                                    colorKey: option.label,
                                  })
                                : i18n.exists(
                                      `isacord_reference.codes.${option.label}`
                                    )
                                  ? t("isacord_reference.label", {
                                      colorKey: option.label,
                                    })
                                  : t(`colors.${option.label}`)
                            }
                            imgSrc={
                              (option as ProductDefinitionColor).textureUrl ??
                              ColorToImage[option.name]
                            }
                          />
                        );
                      })}
                      {enableCustomColorSelection &&
                        (isAdmin || showColorPicker) && (
                          <ColorBox
                            colorOrCombo={
                              "conic-gradient(from -1deg at 53.13% 54.69%, #F00 0deg, #FF0 70.19999742507935deg, #0F0 135deg, #0FF 196.2000060081482deg, #00F 261.00000858306885deg, #F0F 316.9938898086548deg)"
                            }
                            onClick={toggleColorPicker}
                            tooltip={t(`colors.custom`)}
                          />
                        )}
                    </Box>
                  </>
                )}
                {searchQuery &&
                  searchQuery.trim().length >= 3 &&
                  searchResults.colors.length > 0 && (
                    <Box
                      display={"flex"}
                      flexWrap={"wrap"}
                      alignItems={"center"}
                      gap={spacing.smallDesktop}
                      px="12px"
                      maxHeight="200px"
                      overflow="auto"
                    >
                      {searchResults.colors.map((option, index) => (
                        <ColorBox
                          key={index}
                          colorOrCombo={option?.hex}
                          isActive={
                            isLayerActive &&
                            option?.name === colorFromChartCurrent?.name
                          }
                          disabled={
                            disableAllColors ||
                            (option as ProductDefinitionColor).hidden ||
                            (option as AssetColor).disabled
                          }
                          hidden={(option as ProductDefinitionColor).hidden}
                          onClick={() => {
                            onChange(option.name);
                            handleClearSearch();
                          }}
                          tooltip={
                            i18n.exists(
                              `pantone_reference.codes.${option.label}`
                            )
                              ? t("pantone_reference.label", {
                                  colorKey: option.label,
                                })
                              : i18n.exists(
                                    `isacord_reference.codes.${option.label}`
                                  )
                                ? t("isacord_reference.label", {
                                    colorKey: option.label,
                                  })
                                : t(`colors.${option.label}`)
                          }
                          imgSrc={
                            (option as ProductDefinitionColor).textureUrl ??
                            ColorToImage[option.name]
                          }
                        />
                      ))}
                    </Box>
                  )}
              </Box>
            </>
          )}
        </Box>
      </ClickAwayListener>
      {isColorPickerOpen && (
        <Box ref={colorPickerRef}>
          <ColorPicker
            anchorEl={colorSelectRef.current}
            value={isHexValue ? currentValue : colorFromChartCurrent?.hex}
            onColorChange={onChange}
            onClose={toggleColorPicker}
            isOpen={isDropdownOpen}
          />
        </Box>
      )}
    </Box>
  );
};

export default ColorSelect;

export const ColorToImage: Record<string, string> = {
  "eco black": EcoBlackSvg,
  "004": Heather004Img,
  "003": Heather003Img,
  "002": Heather002Img,
};
